import { html, View } from 'rune-ts';
import klass from './ProductRankingBody.module.scss';
import { Tabs, TabVariants } from '../../../../components/cells/Tabs/Tabs';
import { ProductCardList } from '../../../../components/cells/ProductCardList/ProductCardList';
import { ProductListApiS } from '../../../../../features/ProductList/outbound/share';
import { CateListTypeS } from '../../../../../features/CateList/outbound/share';
import {
  bestProductTypeMap,
  type ProductCardData,
  ProductCardSize,
} from '../../../../../features/ProductList/type';
import { BaseLayoutOptions } from '../../../../../features/Core/service';
import { ProductRankingCoverBannerView } from '../ProductRankingBannerView/ProductRankingCoverBanner';
import { CateListTabsController } from '../../utils/CateListTabsController.ts';
import { ProductRankingQueryParams, validateRankType } from '../../utils/validate';
import { delay } from '@fxts/core';
import { QueryController } from '../../../../../shared/util/QueryController';
import { ExcludeSoldOutInput } from '../ExcludeSoldOutInput';
import { CoreUiS } from '@services/MarppleShop/features/Core/outbound/share.ts';
import { MarppleShopLoader } from '@services/MarppleShop/shared/client/loader.ts';
import { TabChangeEvent } from '@services/MarppleShop/renderApp/components/cells/Tabs/events.ts';
import { SectionTabs } from '@services/MarppleShop/shared/components/atoms/SectionTabs/SectionTabs.ts';
import { SectionTitle } from '@services/MarppleShop/renderApp/components/cells/SectionTitle/SectionTitle.ts';
import { generateProductRankingUrl } from '@services/MarppleShop/renderApp/pages/ProductRanking/utils/generateProductRankingUrl.ts';
import { font } from '@services/MarppleShop/shared/typography';

type ProductRankingBodyData = {
  readonly best_products: ProductCardData[];
  readonly updated_at: Date | null;
  readonly recent_product?: ProductCardData;
  readonly cate_lists: CateListTypeS.LegacyCateList[];
  readonly layout_options: BaseLayoutOptions;

  readonly query: {
    cate_list_id?: number;
  };
};

export type ProductRankingPageData = ProductRankingBodyData & {
  readonly query: ProductRankingQueryParams;
};

export class ProductRankingBody extends View<ProductRankingBodyData> {
  private readonly i18n = {
    exclude_sold_out: ET('mps2::product::filter::without_sold_out'),
    exclude_sold_out_short: ET('mps2::product::filter::without_sold_out_short'),
  };

  private readonly queryController: QueryController<ProductRankingQueryParams>;

  private readonly cateListTabsView: Tabs;

  private readonly coverBannerView = new ProductRankingCoverBannerView({
    is_mobile: this.is_mobile,
    last_updated_at: this.data.updated_at,
  });

  private readonly productCardListView = new ProductCardList(
    { products: this.data.best_products },
    {
      resize_width: 600,
      is_lazy: false,
      container_klass: klass.products,
      size: this.is_mobile ? ProductCardSize.small : ProductCardSize.large,
      grid: this.is_mobile
        ? {
            columns: 2,
            column_gap: 16,
            row_gap: 24,
          }
        : {
            columns: 5,
            column_gap: 16,
            row_gap: 32,
          },
    },
  );

  private readonly excludeSoldOutCheckboxView: ExcludeSoldOutInput;

  private readonly sectionTitleView: SectionTitle;

  constructor(data: ProductRankingPageData) {
    super(data);

    this.queryController = new QueryController(data.query, ['rank_type', 'exclude_sold_out', 'cate_list_id']);

    this.cateListTabsView = new Tabs({
      tabs: CateListTabsController.getTabItems(this.data.cate_lists).map((tab) => {
        return {
          ...tab,
          klass: font('14_medium'),
        };
      }),
      current_tab_id: this.data.query.cate_list_id ? this.data.query.cate_list_id.toString() : '',
      custom_klass: klass.catelist_tabs_wrap,
      mo_no_underline: true,
      variants: TabVariants.background,
    });

    this.excludeSoldOutCheckboxView = new ExcludeSoldOutInput({
      name: 'exclude_sold_out',
      label: this.i18n.exclude_sold_out_short,
      is_mobile: this.is_mobile,
      active: this.queryController.getQuery().exclude_sold_out,
    });

    this.sectionTitleView = new SectionTitle({
      title_props: {
        title: new SectionTabs({
          tabs: [
            {
              id: bestProductTypeMap.realtime,
              label: ET('mps2::ranking::realtime'),
              href: generateProductRankingUrl(ET.lang, { rank_type: bestProductTypeMap.realtime }),
              preventDefault: true,
            },
            {
              id: bestProductTypeMap.weekly,
              label: ET('mps2::ranking::weekly'),
              href: generateProductRankingUrl(ET.lang, { rank_type: bestProductTypeMap.weekly }),
              preventDefault: true,
            },
            {
              id: bestProductTypeMap.monthly,
              label: ET('mps2::ranking::monthly'),
              href: generateProductRankingUrl(ET.lang, { rank_type: bestProductTypeMap.monthly }),
              preventDefault: true,
            },
          ],
          current_tab_id: this.queryController.getQuery().rank_type,
          size: data.layout_options.is_mobile ? 'xSmall' : 'small',
        }),
        size: data.layout_options.is_mobile ? 'xSmall' : 'small',
      },
    });
  }

  protected override template() {
    return html`
      <div class="${klass.body}">
        <!-- Title -->
        <div class="${klass.title_section}">
          <div class="${klass.page_title}">
            ${CoreUiS.makeShoppingTitle(
              this.data.layout_options.is_mobile,
              'product_ranking',
              ET('mps2::main::weekly_best_product::description'),
            )}
          </div>

          <!-- 실시간, 주간, 월간 Tabs -->
          <div class="${klass.tabs_wrap}">
            <div>${this.sectionTitleView}</div>
            ${this.is_mobile
              ? html`<div class="${klass.checkbox_wrap}">${this.excludeSoldOutCheckboxView}</div>`
              : ''}
          </div>
        </div>

        <!-- 배너 -->
        <div class="${klass.banner_wrap}">
          ${this.coverBannerView}

          <!-- 품절 상품 제외 -->
          <div class="${klass.checkbox_wrap}">${this.is_mobile ? '' : this.excludeSoldOutCheckboxView}</div>
        </div>

        <!-- 대카테고리 Tabs -->
        <div class="${klass.category_wrap}">${this.cateListTabsView}</div>

        <!-- 상품 목록 -->
        ${this.productCardListView}
      </div>
    `;
  }

  protected override onRender() {
    if (this.is_mobile) {
      this.subView(Tabs)?.scrollIntoView({ block: 'nearest', inline: 'center' });
    }

    // 품절 상품 제외 변경
    this.delegate(ExcludeSoldOutInput.InputChangedEvent, ExcludeSoldOutInput, async (e) => {
      this.queryController
        .setQuery({
          exclude_sold_out: e.detail,
        })
        .syncQuery();

      await this.updateProductList();
    });

    // 실시간, 월간, 주간 변경
    this.delegate(SectionTabs.ChangeTabEvent, SectionTabs, async (e) => {
      this.queryController
        .setQuery({
          rank_type: validateRankType(e.detail.tabId),
        })
        .syncQuery();

      await this.updateProductList();
    });

    // 대카테고리 변경
    this.cateListTabsView.addEventListener(TabChangeEvent, async (e) => {
      const { tabId } = e.detail;
      const cate_list_id = tabId === '' ? undefined : parseInt(tabId);

      // query 업데이트
      this.queryController.setQuery({ cate_list_id }).syncQuery();

      // 상품 목록 갱신
      await this.updateProductList();
    });
  }

  private get is_mobile(): boolean {
    return this.data.layout_options.is_mobile;
  }

  private async updateProductList() {
    const fetchPromise = ProductListApiS.getBestProductListApi({
      ...this.queryController.getQuery(),
      include_watcher_count: true,
      limit: 100,
    });

    // 200ms 이내로 들어오면 loading 없이 보여주기
    const result = await Promise.race([fetchPromise, delay(200, 'loading')]);

    if (result === 'loading') {
      MarppleShopLoader.start();
    }

    const response = await fetchPromise;

    // 상품 목록 갱신
    this.productCardListView.setProductsData(response.products).redraw();

    // 마지막 업데이트 시간 갱신
    this.coverBannerView.setUpdatedAt(response.updated_at);
    this.coverBannerView.redraw();

    MarppleShopLoader.end();
  }
}
